import { useQuery } from '@tanstack/react-query';

import { listWorkspaceSecrets } from '@/lib/api/generated/api';
import type { WorkspaceSecretView } from '@/lib/api/generated/model';

import { secretKeys } from '../queryKeys';

/**
 * Every secret and variable defined on a workspace, in the service's order.
 *
 * Anything but a 200 throws rather than resolving to an empty list. That
 * distinction is the whole point here: the caller has to be able to tell "this
 * workspace has none" from "we could not find out", and collapsing the two into
 * `[]` renders a confident empty state over a failed request — which invites
 * someone to add a key that already exists and meet a conflict they cannot
 * explain (FR-003).
 *
 * The order is the service's (by key) and is not re-sorted here. A list that
 * reshuffles between renders makes a visual diff meaningless.
 */
export function useWorkspaceSecrets(workspaceId: string) {
  const query = useQuery({
    queryKey: secretKeys.list(workspaceId),
    queryFn: async (): Promise<WorkspaceSecretView[]> => {
      const response = await listWorkspaceSecrets(workspaceId);
      if (response.status !== 200) {
        throw new Error('The secrets and variables could not be loaded.');
      }
      return response.data.items;
    },
    retry: false,
  });

  return {
    entries: query.data,
    isPending: query.isPending,
    loadFailed: query.isError,
    refetch: query.refetch,
  };
}
